import cx from 'classnames';
import React from 'react';
import * as qf from '../misc/qf.js';
import DateInput from '../misc/DateInput.js';
import scrollToErroredControls from '../misc/scrollToErroredControls.js';
import {firstOnly} from '../misc/rerr.js';

const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

function thirteenYearsOld(now, birthdate) {
  now.setFullYear(now.getFullYear() - 13);
  if (!birthdate) {
    return false;
  }
  let [yyyy, mm, dd] = birthdate.split('-');
  yyyy = parseInt(yyyy, 10);
  if (yyyy < 100) {
    yyyy = yyyy + 1900;
  }
  mm = parseInt(mm, 10);
  dd = parseInt(dd, 10);
  let date = new Date(yyyy,mm-1,dd);
  if (isNaN(date)) {
    return false;
  }
  if ((date.getFullYear() == yyyy) && (date.getMonth() == mm - 1) && (date.getDate() == dd)){
    if(date.getTime() <= now.getTime()){
      return true;
    }
  }
  return false;
}

export default class RegistrationForm extends React.Component {
  state = {
    errors: {},
    failed: false,
    params: {
      birthdate: '',
      email: '',
      email_confirmation: '',
      first_name: '',
      last_name: '',
      password: '',
      password_confirmation: '',
      terms: true,
      flow: this.props.flow,
      return_to: this.props.returnTo,
    },
    submissionCount: 0,
    submitting: false,
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.submissionCount > prevState.submissionCount) {
      if (Object.keys(this.state.errors).length > 0) {
        scrollToErroredControls(this._form);
      }
    }
  }

  _checkEmail = async (email) => {
    try {
      let data = await qf.getJSON('/register/check_email?email=' + email);
      if (data && data.errors) {
        return firstOnly(data.errors);
      }
      return {};
    } catch (err) {
      return {
        email: true,
      }
    }
  };

  _checkForErrors = () => {
    const errors = {};
    const params = this.state.params;
    if (!params.first_name.trim()) {
      errors.first_name = true;
    }
    if (!params.last_name.trim()) {
      errors.last_name = true;
    }
    if (!params.email.trim()) {
      errors.email = true;
    } else if (!params.email.trim().match(emailPattern)) {
      errors.email = 'Invalid email';
    }
    if (params.email.trim() !== params.email_confirmation.trim()) {
      errors.email_confirmation = 'Must match email';
    }
    if (params.password.trim().length < 6) {
      errors.password = 'Must be at least 6 characters';
    }
    if (!params.password_confirmation.trim()) {
      errors.password_confirmation = true;
    }
    if (params.password.trim() !== params.password_confirmation.trim()) {
      errors.password_confirmation = 'Must match password';
    }
    if (!thirteenYearsOld(new Date(), params.birthdate)) {
      errors.age = 'Must be 13 years old or older';
    }
    if (params.gender !== 'male' && params.gender !== 'female') {
      errors.gender = true;
    }
    return errors;
  };

  _dateChanged = (date) => {
    this.setState({
      params: {
        ...this.state.params,
        birthdate: date,
      },
    });
  };

  _emailChanged = (event) => {
    const next = {
      params: {
        ...this.state.params,
        email: event.target.value,
      },
    };
    if (next.params.email !== this.state.params.email && this.state.errors.email) {
      next.errors = {...this.state.errors};
      delete next.errors.email;
    }
    this.setState(next);
  };

  _emailBlurred = (event) => {
    if (!this.state.params.email) {
      return;
    }
    this._checkEmail(this.state.params.email).then(errors => {
      errors = {...this.state.errors, ...errors};
      if (errors.email_confirmation && this.state.params.email === this.state.params.email_confirmation) {
        delete errors.email_confirmation;
      }
      this.setState({
        errors: errors,
      });
    });
  }

  _paramChanged = (event) => {
    this.setState({
      params: {
        ...this.state.params,
        [event.target.name]: event.target.value,
      },
    });
  };

  _submit = async (event) => {
    event.preventDefault();
    this.setState({failed: false, submitting: true});
    let errors = await this._checkEmail(this.state.params.email);
    errors = {...errors, ...(this._checkForErrors())};
    if (Object.keys(errors).length > 0) {
      this.setState(prev => {
        return {
          errors: errors,
          submitting: false,
          submissionCount: prev.submissionCount + 1,
        };
      });
      return;
    }
    this.setState({errors: {}});
    try {
      let data = await qf.postJSON('/register', {new_user: this.state.params});
      window.location.href = data.redirect_to;
    } catch (error) {
      let data = null;
      if (error.response && error.response.status === 422) {
        data = await error.response.json();
      }
      this.setState(prev => {
        const next = {
          failed: true,
          submitting: false,
          submissionCount: prev.submissionCount + 1,
        };
        if (data && data.errors) {
          next.errors = firstOnly(data.errors);
        }
        return next;
      });
    }
  };

  _submitReady = (event) => {
    return !this.state.submitting;
  };

  render() {
    const {errors, params} = this.state;
    return (
      <form ref={n => this._form = n} autoComplete="off" onSubmit={this._submit}>
        <div className={cx('form-group', {'has-error': errors.first_name || errors.last_name})}>
          <div className="row">
            <div className="col-xs-6">
              <label className="control-label">First Name</label>
              <input type="text" className="form-control" name="first_name" autoFocus autoCapitalize="on" autoCorrect="off" required value={params.first_name} onChange={this._paramChanged} />
              {
                errors.first_name && errors.first_name !== true ? (
                  <div className="help-block">{errors.first_name}</div>
                ): null
              }
            </div>
            <div className="col-xs-6">
              <label className="control-label">Last Name</label>
              <input type="text" className="form-control" name="last_name" autoCapitalize="on" autoCorrect="off" required value={params.last_name} onChange={this._paramChanged}  />
              {
                errors.last_name && errors.last_name !== true ? (
                  <div className="help-block">{errors.last_name}</div>
                ): null
              }
            </div>
          </div>

        </div>

        <div className={cx('form-group', {'has-error': errors.email || errors.school_district_email})}>
          <label className="control-label">Email Address</label>
          <input type="email" className="form-control" name="email" required value={params.email} onChange={this._emailChanged} onBlur={this._emailBlurred} />
          {
            errors.email && errors.email !== true ? (
              <div className="help-block">{errors.email}</div>
            ): errors.school_district_email ? (
              <div className="help-block">
                Please use a different email address that is not school-issued.
                If you believe this is in error, please contact us at <a href="mailto:support@blastathletics.com">support@blastathletics.com</a>
              </div>
            ): null
          }
        </div>

        <div className={cx('form-group', {'has-error': errors.email_confirmation})}>
          <label className="control-label">Confirm Email</label>
          <input type="email" className="form-control" name="email_confirmation" required value={params.email_confirmation} onChange={this._paramChanged} />
          {
            errors.email_confirmation && errors.email_confirmation !== true ? (
              <div className="help-block">{errors.email_confirmation}</div>
            ): null
          }
        </div>

        <div className="row">
          <div className="col-xs-6">
            <div className={cx('form-group', {'has-error': errors.password})}>
              <label className="control-label">Password</label>
              <input type="password" className="form-control" name="password" required value={params.password} onChange={this._paramChanged} />
              {
                errors.password && errors.password !== true ? (
                  <div className="help-block">{errors.password}</div>
                ): null
              }
            </div>
          </div>
          <div className="col-xs-6">
            <div className={cx('form-group', {'has-error': errors.password_confirmation})}>
              <label className="control-label">Confirm Password</label>
              <input type="password" className="form-control" name="password_confirmation" required value={params.password_confirmation} onChange={this._paramChanged} />
              {
                errors.password_confirmation && errors.password_confirmation !== true ? (
                  <div className="help-block">{errors.password_confirmation}</div>
                ): null
              }
            </div>
          </div>
        </div>

        <div className={cx('form-group', {'has-error': errors.age})}>
          <label className="control-label">Date of Birth</label>
          <div className="row">
            <div className="col-xs-12 col-sm-6">
              <DateInput forceFallback required max={`${new Date().getFullYear()}-01-01`} value={params.birthdate} onChange={this._dateChanged} />
            </div>
          </div>
          {
            errors.age && errors.age !== true ? (
              <div className="help-block">{errors.age}</div>
            ): null
          }
        </div>

        <div className={cx('form-group', {'has-error': errors.gender})}>
          <label className="control-label">Gender</label>
          <div className="form-inline">
            <div className="radio">
              <label>
                <input type="radio" name="gender" value="male" required checked={params.gender === 'male'} onChange={this._paramChanged} /> Male
              </label>
            </div>
            <span className="hidden-xs">&nbsp;&nbsp;&nbsp;&nbsp;</span>
            <div className="radio">
              <label>
                <input type="radio" name="gender" value="female" required checked={params.gender === 'female'} onChange={this._paramChanged} /> Female
              </label>
            </div>
          </div>
        </div>

        <div className="form-group">
          By creating an account, you agree to our <a href="/terms" target="_blank" tabIndex="-1">Terms of Use</a>.
        </div>

        <div className="form-group clearfix">
          <div className="pull-right">
            <button type="submit" className="btn btn-primary" disabled={!this._submitReady()}>
              Register
            </button>
          </div>
          {
            this.state.failed ? (
              <div className="btn-txt text-danger">There was an errror</div>
            ): this.state.submitting ? (
              <div className="btn-txt loading-gif" />
            ): null
          }
        </div>
      </form>
    );
  }
}
